import React from "react";
import { IconEye } from "@tabler/icons-react";
import NoDataFound from "./NoDataFound";

type Column<T> = {
  label: string | React.ReactNode;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  children?: Column<T>[];
  width?: string | number;
};

interface ListViewProps<T> {
  data: T[];
  columns: Column<T>[];
  multiData?: T[];
  onRowClick?: (item: T) => void;
  onRowDoubleClick?: (item: T) => void;
  mainDivClassName?: string;
  mailDivClassName?: string;
  trClassName?: string;
  tdClassName?: string;
  tbodyClass?: string;
  tbodyClassName?: string;
  thClassName?: string;
  tableType?: string;
  ActionClassName?: string;
  TableClassName?: string;
  renderExpandedContent?: (data: T) => React.ReactNode;
  expandedRows?: Set<number>;
  scrollbarClassName?: string;
  splitToggle?: boolean;
}

function ListView<T>({
  data,
  columns,
  multiData,
  onRowClick,
  onRowDoubleClick,
  TableClassName,
  tableType,
  mailDivClassName = "bg-[#FCFDF8] p-0 rounded-b-none rounded-t-none",
  mainDivClassName = "bg-transparent",
  trClassName = "hover:bg-[#FBD037]/10 group transition-colors duration-150 cursor-pointer",
  thClassName = "bg-[#E0E0E0]",
  tbodyClass = "",
  tbodyClassName = "",
  tdClassName = "",
  ActionClassName,
  splitToggle,
  renderExpandedContent,
  expandedRows,
  scrollbarClassName = "hide-scrollbar",
}: ListViewProps<T>) {
  const hasGroups = columns.some((c) => c.children && c.children.length > 0);
  const hasExpandedContent = !!renderExpandedContent;
  const headerBgClass = thClassName.includes("bg-") ? "" : "bg-[#E0E0E0]";
  const resolvedTbodyClass = tbodyClass || tbodyClassName || "";

  return (
    <div className={`${mainDivClassName}`}>
      <div
        className={`overflow-auto max-h-[70vh] ${scrollbarClassName} ${mailDivClassName}`}
      >
        <table className={`w-full border border-dashed border-gray-300 whitespace-nowrap ${TableClassName || ""}`}>
          <thead
            className={`text-[#212121]/90 uppercase tracking-wider border-b border-t border-dashed border-gray-300 sticky top-0 z-20 shadow-sm ${headerBgClass} ${thClassName}`}
          >
            {/* Top row */}
            <tr>
              {columns.map((col, i) => {
                const isGroup = col.children && col.children.length > 0;
                const isFirst = i === 0;
                const align =
                  thClassName.includes("text-center") ||
                  col.className?.includes("text-center") ||
                  (col as any).headerClassName?.includes("text-center")
                    ? "text-center"
                    : "text-left";

                return (
                  <th
                    key={i}
                    colSpan={isGroup ? col.children!.length : 1}
                    rowSpan={isGroup ? 1 : hasGroups ? 2 : 1}
                    style={col.width ? { width: col.width } : undefined}
                    className={`font-bold text-xs uppercase tracking-wider ${
                      isFirst ? "px-1.5 w-9 text-center" : "px-4"
                    } ${align} py-1.5 border-r last:border-none border-dashed border-gray-300 select-none ${
                      isGroup ? "border-b border-dashed" : ""
                    } ${col.className || ""}`}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>

            {/* Second row for grouped subheaders */}
            {hasGroups && (
              <tr>
                {columns.flatMap((col, i) =>
                  col.children
                    ? col.children.map((child, j) => {
                        const childAlign =
                          child.className?.includes("text-center") ||
                          (child as any).headerClassName?.includes("text-center") ||
                          col.className?.includes("text-center")
                            ? "text-center"
                            : "text-left";
                        return (
                          <th
                            key={`${i}-${j}`}
                            style={child.width ? { width: child.width } : undefined}
                            className={`${childAlign} font-bold text-xs uppercase tracking-wider px-4 border-r border-dashed border-gray-300 text-[#212121]/80 ${
                              child.className || ""
                            }`}
                          >
                            {child.label}
                          </th>
                        );
                      })
                    : []
                )}
              </tr>
            )}
          </thead>

          <tbody className={`divide-y divide-gray-300 divide-dashed ${resolvedTbodyClass}`}>
            {!data || data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.reduce(
                    (total, col) =>
                      total + (col.children ? col.children.length : 1),
                    0
                  )}
                  className="p-0 border-b border-gray-300"
                >
                  <NoDataFound
                    title="No Data Found"
                    message=""
                    mainClassName=""
                    subClassName=""
                  />
                </td>
              </tr>
            ) : (
              data.map((item, idx) => {
                const key = (item as any)?.id?.toString?.() ?? idx.toString();
                const idNum = parseInt(key, 10);
                const isExpanded = hasExpandedContent && expandedRows?.has(idNum);

                return (
                  <React.Fragment key={key}>
                    {/* Main row */}
                    <tr
                      onClick={() => onRowClick?.(item)}
                      onDoubleClick={() => onRowDoubleClick?.(item)}
                      className={`cursor-pointer ${trClassName} ${
                        isExpanded ? "bg-gray-50 " : ""
                      }`}
                    >
                      {columns.flatMap((col, i) => {
                        const isFirst = i === 0;
                        const cellAlign = tdClassName.includes("text-center")
                          ? "text-center"
                          : "text-left";
                        if (col.children && col.children.length > 0) {
                          return col.children.map((child, j) => (
                            <td
                              key={`${i}-${j}`}
                              style={
                                child.width
                                  ? { width: child.width }
                                  : col.width
                                  ? { width: col.width }
                                  : undefined
                              }
                              className={`${
                                isFirst && j === 0
                                  ? "px-1.5 w-9 text-center"
                                  : "px-4"
                              } ${cellAlign} py-3 text-[#212121] border-r border-dashed border-gray-300 text-xs font-medium tracking-wider ${tdClassName} ${
                                col.className || ""
                              }`}
                            >
                              {child.render ? child.render(item, idx) : null}
                            </td>
                          ));
                        }
                        return (
                          <td
                            key={i}
                            style={col.width ? { width: col.width } : undefined}
                            className={`${
                              isFirst ? "px-1.5 w-9 text-center" : "px-4"
                            } ${cellAlign} border-r last:border-none border-dashed py-3 text-[#212121] text-xs font-medium tracking-wider ${tdClassName} ${
                              col.className || ""
                            }`}
                          >
                            {col.render ? col.render(item, idx) : null}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Expanded content row */}
                    {isExpanded && renderExpandedContent && (
                      <tr>
                        <td
                          colSpan={
                            (hasExpandedContent ? 1 : 0) +
                            columns.reduce(
                              (total, col) =>
                                total +
                                (col.children ? col.children.length : 1),
                              0
                            )
                          }
                          className="p-0 border-b border-gray-300"
                        >
                          {renderExpandedContent(item)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}

            {splitToggle && (
              <>
                {multiData?.map((item: any, idx: number) => (
                  <tr key={`split-${idx}`} className="bg-[#FCFDF8] border-t border-dashed border-gray-300 ">
                    <td className="text-[#212121] h-[67px]">
                      <div className="flex justify-center items-center h-full">
                        <div className="w-px h-16 bg-gray-300"></div>
                        <div className="w-3/6 h-px bg-gray-300"></div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-[#212121] text-xs">#04564665-1</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">SKU123</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">Alok Kumar</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">25</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">Self</td>
                    <td className="px-6 py-3 text-[#212121] text-xs">
                      4 Aug 2025, 6:40 AM
                    </td>
                    <td className="px-6 py-3 text-[#212121] text-xs">
                      <div className="flex justify-center">
                        <span className="px-4 py-1.5 text-xs font-medium rounded-full bg-[#FF4848]/20 border border-[#FF4848]">
                          Pending
                        </span>
                      </div>
                    </td>
                    <td className="text-center px-6 py-3 text-[#212121] text-xs">
                      <div className="text-center">
                        <button className="p-2 border rounded-full bg-[#F7F6F2]">
                          <IconEye size={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ListView;